/**
 * A chain's steps, in the Timelines section (2026-10-23, OPVisual's ask x4 item 2: "a short chain shows as a list you
 * drag to reorder"; the ruler stays for song-length timelines). Each row is one step - FIRE a named action, or WAIT
 * so long before the next - and the list runs top to bottom on one press (`timeline/chain.ts`, OPBridge `"tier":
 * "chain"`). Built only from the Navigate kit (DEC-255); the drag grip is the kit's `nv-grip`.
 *
 * What a step can fire, without OPBridge's full action list (our pairing may fire any action but not list them,
 * OPBridge `opbuttons-full`): OPVisual's columns (always published) and its named cells (its grid listing, as the
 * timeline editor's OPVisual chooser reads it), OPBridge's built-in "Panic everything", or any action by its id.
 */
import { For, Show, createMemo, createSignal, onCleanup, type JSX } from 'solid-js';
import type { Timeline, TimelineStep, WaitStep, WaitUnit } from '@opbuttons/schema';
import { getTimeline, upsertTimeline } from '../timeline/store.ts';
import { chainSteps, firedActionId, isWaitStep, moveChainStep, newFire, newWait, waitWords, withChainSteps, CHAIN_MAX_MS } from '../timeline/chain.ts';
import { timelinePlacement, toBridgeTimeline } from '../timeline/bridgeSync.ts';
import { autoPickOpVisualBridge } from '../opvisual/machine.ts';
import { cellKey, opvListing, watchOpvListing } from '../opvisual/listing.ts';
import { ulid } from '../utils/ulid.ts';

const OPV_COLUMNS = 10;
const UNITS: { unit: WaitUnit; word: string }[] = [
  { unit: 'ms', word: 'ms' }, { unit: 'beats', word: 'Beats' }, { unit: 'bars', word: 'Bars' }, { unit: 'nextBar', word: 'Next bar' },
];
const PANIC_ID = 'panic-everything';
const OTHER = '__other__';

interface Choice { id: string; label: string; group: string }

export function ChainEditor(props: { id: string; onBack: () => void; onPlay: (t: Timeline) => void; playing: boolean }): JSX.Element {
  const t = createMemo(() => getTimeline(props.id));
  const steps = createMemo(() => { const x = t(); return x ? chainSteps(x) : []; });
  const save = (next: TimelineStep[]) => { const x = t(); if (x) upsertTimeline(withChainSteps(x, next)); };
  const patch = (i: number, p: Partial<TimelineStep>) => save(steps().map((s, k) => (k === i ? ({ ...s, ...p } as TimelineStep) : s)));
  const move = (from: number, to: number) => { const x = t(); if (x) upsertTimeline(moveChainStep(x, from, to)); };

  // What a step can fire. OPVisual's grid as it reports itself, from the machine it runs on.
  const target = autoPickOpVisualBridge() ?? undefined;
  onCleanup(watchOpvListing(target));
  const choices = createMemo<Choice[]>(() => {
    const listing = opvListing(target);
    const cols = Math.max(1, Math.min(OPV_COLUMNS, listing?.cols ?? OPV_COLUMNS));
    const out: Choice[] = Array.from({ length: cols }, (_, i) => ({ id: `opvisual-col-${i + 1}`, label: `OPVisual column ${i + 1}`, group: 'OPVisual columns' }));
    for (let row = 1; row <= (listing?.rows ?? 0); row++) {
      for (let col = 1; col <= cols; col++) {
        const name = listing?.cells[cellKey(col, row)]?.label;
        if (name) out.push({ id: `opvisual-cell-l${row}-${col}`, label: `L${row}-${col}: ${name}`, group: 'OPVisual cells' });
      }
    }
    out.push({ id: PANIC_ID, label: 'Panic everything', group: 'OPBridge' });
    return out;
  });
  const labelOf = (id: string) => choices().find((c) => c.id === id)?.label ?? id;

  // What OPBridge would be sent, on the machine the chain plays on: per-step reasons, and the chain's own.
  const home = createMemo(() => { const x = t(); return x ? timelinePlacement(x).home : null; });
  const compiled = createMemo(() => { const x = t(); const h = home(); return x && h ? toBridgeTimeline(x, h) : null; });
  const topNote = createMemo(() => {
    if (!home()) return 'No computer to play it on yet: pair one in Computers.';
    const c = compiled();
    if (steps().length === 0) return null;
    return c?.wire ? null : c?.droppedWarning ?? null;
  });

  // Drag to reorder by the grip: the row under the pointer is where it lands.
  const [drag, setDrag] = createSignal<{ from: number; over: number } | null>(null);
  let listEl: HTMLDivElement | undefined;
  const indexAt = (y: number): number => {
    const rows = [...(listEl?.querySelectorAll<HTMLElement>('[data-testid="chain-step"]') ?? [])];
    for (let i = 0; i < rows.length; i++) { const r = rows[i]!.getBoundingClientRect(); if (y < r.top + r.height / 2) return i; }
    return Math.max(0, rows.length - 1);
  };
  const gripDown = (i: number) => (e: PointerEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ from: i, over: i });
  };
  const gripMove = (e: PointerEvent) => { const d = drag(); if (d) setDrag({ ...d, over: indexAt(e.clientY) }); };
  const gripUp = () => { const d = drag(); setDrag(null); if (d && d.over !== d.from) move(d.from, d.over); };

  const addFire = () => { const c = choices()[0]!; save([...steps(), newFire(ulid(), c.id, c.label)]); };
  const addWait = () => save([...steps(), newWait(ulid(), 'ms', 300)]);

  const FireRow = (rp: { s: TimelineStep; i: number }) => {
    const id = () => firedActionId(rp.s) ?? '';
    const known = () => choices().some((c) => c.id === id());
    const [other, setOther] = createSignal(!known() && id() !== '');
    const groups = () => [...new Set(choices().map((c) => c.group))];
    return (
      <>
        <b>Fire</b>
        <select class="nv-select" data-testid="chain-action" aria-label={`Step ${rp.i + 1}: what it fires`} value={other() || !known() ? OTHER : id()}
          onChange={(e) => {
            const v = e.currentTarget.value;
            if (v === OTHER) { setOther(true); return; }
            setOther(false);
            patch(rp.i, { action: { module: 'action', command: 'fire', args: { id: v } }, label: labelOf(v) } as Partial<TimelineStep>);
          }}>
          <For each={groups()}>{(g) => (
            <optgroup label={g}><For each={choices().filter((c) => c.group === g)}>{(c) => <option value={c.id}>{c.label}</option>}</For></optgroup>
          )}</For>
          <option value={OTHER}>Another action, by its id</option>
        </select>
        <Show when={other() || !known()}>
          <span class="nv-fld" data-nav-layer><input data-testid="chain-action-id" aria-label="The action's id" placeholder="its id, e.g. opvisual-col-3" value={id()}
            onChange={(e) => { const v = e.currentTarget.value.trim(); if (v) patch(rp.i, { action: { module: 'action', command: 'fire', args: { id: v } }, label: labelOf(v) } as Partial<TimelineStep>); }} /></span>
        </Show>
      </>
    );
  };

  const WaitRow = (rp: { s: WaitStep; i: number }) => (
    <>
      <b>Wait</b>
      <Show when={rp.s.unit !== 'nextBar'}>
        <span class="nv-fld nv-fld-num" data-nav-layer>
          <input type="number" data-testid="chain-wait-amount" aria-label={`Step ${rp.i + 1}: how long`} min="0" step={rp.s.unit === 'ms' ? '10' : '0.25'}
            max={rp.s.unit === 'ms' ? String(CHAIN_MAX_MS) : undefined} value={rp.s.amount ?? ''}
            onChange={(e) => { const n = Number(e.currentTarget.value); patch(rp.i, { amount: Number.isFinite(n) ? n : 0 } as Partial<TimelineStep>); }} />
        </span>
      </Show>
      <div class="nv-seg" data-testid="chain-wait-unit" role="group" aria-label={`Step ${rp.i + 1}: wait in`}>
        <For each={UNITS}>{(u) => (
          <button type="button" aria-pressed={rp.s.unit === u.unit} data-unit={u.unit}
            onClick={() => {
              if (u.unit === rp.s.unit) return;
              const keep = rp.s.unit !== 'nextBar' && u.unit !== 'nextBar' && (u.unit === 'ms') === (rp.s.unit === 'ms');
              save(steps().map((s, k) => (k === rp.i ? newWait(s.id, u.unit, keep ? rp.s.amount : undefined) : s)));
            }}>{u.word}</button>
        )}</For>
      </div>
    </>
  );

  return (
    <div class="nv-ss" data-testid="chain" data-id={props.id}>
      <div class="nv-row">
        <button type="button" class="nv-b nv-b-q nv-b-sm" data-testid="chain-back" onClick={() => props.onBack()}>{'‹'} Timelines</button>
        <span class="nv-sp" />
        <button type="button" class="nv-b nv-b-pri nv-b-sm" data-testid="chain-play" disabled={!t() || !compiled()?.wire}
          onClick={() => { const x = t(); if (x) props.onPlay(x); }}>{props.playing ? 'Stop' : 'Play'}</button>
      </div>
      <div class="nv-ss-h">
        <h2>{t()?.name ?? 'Chain'}</h2>
        <p>One press runs these top to bottom. A wait in ms runs with the music stopped; beats and bars wait for it.{home() ? ` Plays on ${home()!.name}.` : ''}</p>
      </div>
      <Show when={topNote()}><p class="nv-note" data-tone="warn" data-testid="chain-note">{topNote()}</p></Show>
      <Show when={steps().length > 0} fallback={<p class="nav-empty" data-testid="chain-empty">Nothing in this chain yet. Add an action, then a wait, then another action.</p>}>
        <div class="nav-well" ref={listEl} data-testid="chain-steps">
          <For each={steps()}>{(s, i) => (
            <div class="nav-lr" data-testid="chain-step" data-kind={s.kind} data-index={i()}
              data-drop-before={drag() && drag()!.over === i() && drag()!.over < drag()!.from ? 'true' : undefined}
              data-drop-after={drag() && drag()!.over === i() && drag()!.over > drag()!.from ? 'true' : undefined}
              data-dragging={drag()?.from === i() ? 'true' : undefined}
              onKeyDown={(e) => {
                if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                e.preventDefault(); move(i(), e.key === 'ArrowUp' ? i() - 1 : i() + 1);
              }}>
              <span class="nv-grip" data-testid="chain-grip" role="button" tabindex={0} aria-label={`Drag step ${i() + 1} to reorder`}
                onPointerDown={gripDown(i())} onPointerMove={gripMove} onPointerUp={gripUp} onPointerCancel={() => setDrag(null)} />
              <div class="nav-lr-main">
                <div class="nv-row">
                  <Show when={isWaitStep(s)} fallback={<FireRow s={s} i={i()} />}><WaitRow s={s as WaitStep} i={i()} /></Show>
                </div>
                <Show when={compiled()?.stepWarnings.get(s.id)}><small class="nv-lib-warn" data-testid="chain-step-warn">{compiled()!.stepWarnings.get(s.id)}</small></Show>
              </div>
              <span class="nav-lr-trail nv-row">
                <button type="button" class="nv-b nv-b-q nv-b-sm" data-testid="chain-up" disabled={i() === 0} aria-label={`Move step ${i() + 1} up`} onClick={() => move(i(), i() - 1)}>{'↑'}</button>
                <button type="button" class="nv-b nv-b-q nv-b-sm" data-testid="chain-down" disabled={i() === steps().length - 1} aria-label={`Move step ${i() + 1} down`} onClick={() => move(i(), i() + 1)}>{'↓'}</button>
                <button type="button" class="nv-b nv-b-q nv-b-sm" data-danger data-testid="chain-remove" aria-label={`Remove step ${i() + 1}`}
                  onClick={() => save(steps().filter((_, k) => k !== i()))}>Remove</button>
              </span>
            </div>
          )}</For>
        </div>
      </Show>
      <div class="nv-row">
        <button type="button" class="nv-b nv-b-sm" data-testid="chain-add-action" onClick={addFire}>+ Action</button>
        <button type="button" class="nv-b nv-b-sm" data-testid="chain-add-wait" onClick={addWait}>+ Wait</button>
        <span class="nv-sp" />
        <small class="nv-lib-sub" data-testid="chain-summary">{steps().map((s) => (isWaitStep(s) ? `wait ${waitWords(s)}` : labelOf(firedActionId(s) ?? '?'))).join(', then ')}</small>
      </div>
    </div>
  );
}
